import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { css } from '@emotion/react'
import Sidebar from 'shared/components/ui/Sidebar'
import { Loader, Form, Dropdown, Segment, Label, Button } from 'util/ui'
import { SortableList } from 'shared/ui'
import SpacedItems from 'shared/components/ui/SpacedItems'
import {
  selectWidgetsIsLoading,
  selectWidgetsWithChat,
} from 'ducks/widgets/selectors'
import { doUpdateSettings } from 'ducks/widgets/operations'
import { doHideChatAvailabilityModal } from 'actions/modals'
import MessageBox from 'subapps/settings/components/Widgets/Settings/MessageBox'

const optionLineStyle = () => theme => css`
  display: flex;
  align-items: center;
  & > .ui.label {
    margin-right: ${theme.spacing['5']} !important;
  }
`

const indicatorStyle = primaryColor => theme => css`
  &&& {
    background-color: ${theme.color.primary[primaryColor]};
    border-color: ${theme.color.primary[primaryColor]};
  }
`

const widgetNameStyle = () => theme => css`
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.color.monochrome.dark} !important;
`

const paragraphStyle = () => theme => css`
  &&& {
    font-size: ${theme.fontSize.x2large};
    color: ${theme.color.monochrome.dark};
    margin-bottom: ${theme.spacing['12']};
  }
`

const containerStyle = () => theme => css`
  width: 100%;
  padding: ${[
    theme.spacing['18'],
    theme.spacing['12'],
    theme.spacing['12'],
    theme.spacing['12'],
  ].join(' ')};
`

const bodyStyle = css`
  display: flex;
`

const options = [
  {
    key: 'online',
    value: 'online',
    text: (
      <span css={optionLineStyle}>
        <Label circular empty css={indicatorStyle('positive')} /> Accepting all
        chats
      </span>
    ),
  },
  {
    key: 'existing',
    value: 'existing',
    text: (
      <span css={optionLineStyle}>
        <Label circular empty css={indicatorStyle('warning')} /> Accepting
        existing chats
      </span>
    ),
  },
  {
    key: 'offline',
    value: 'offline',
    text: (
      <span css={optionLineStyle}>
        <Label circular empty css={indicatorStyle('negative')} /> Disabled for
        all chats
      </span>
    ),
  },
]

const sortHeaders = [
  {
    title: 'Widgets',
    key: 'title',
    sortKey: 'title',
    sortable: true,
  },
  {
    title: 'Visibility',
    key: 'visibility',
    sortKey: 'visibility',
    sortable: false,
    flexBasis: '240px',
    gap: 'auto',
  },
]

const AvailabilityEditor = () => {
  const widgets = useSelector(selectWidgetsWithChat)
  const isLoading = useSelector(selectWidgetsIsLoading)
  const [availability, setAvailability] = useState({})
  const dispatch = useDispatch()
  const handleClose = useCallback(
    () => {
      dispatch(doHideChatAvailabilityModal())
    },
    [dispatch]
  )

  const handleChange = useCallback(
    (_, { name, value }) => {
      setAvailability(currentAvailability => {
        return {
          ...currentAvailability,
          [name]: value,
        }
      })
    },
    [setAvailability]
  )

  const handleSubmit = useCallback(
    () => {
      widgets.forEach(widget => {
        const value = availability[widget.id]
        if (value !== widget.publishedSettings['chat.availability']) {
          dispatch(
            doUpdateSettings({ 'chat.availability': value }, widget.id, {
              commitType: 'MERGE',
            })
          )
        }
      })
      handleClose()
    },
    [dispatch, widgets, availability, handleClose]
  )

  useEffect(
    () => {
      setAvailability(currentAvailability => {
        const newAvailability = { ...currentAvailability }
        let changed = false
        widgets.forEach(widget => {
          if (!currentAvailability[widget.id]) {
            changed = true
            newAvailability[widget.id] =
              widget.publishedSettings['chat.availability']
          }
        })
        if (changed) return newAvailability
        return currentAvailability
      })
    },
    [widgets]
  )
  return (
    <Sidebar
      wide
      onClose={handleClose}
      open
      headerChildren="Set Chat Availability"
      footerChildren={
        <Button fluid primary onClick={handleSubmit}>
          Save changes
        </Button>
      }
      bodyCss={bodyStyle}
    >
      <div css={containerStyle}>
        {isLoading ? (
          <Loader active inline="centered" />
        ) : (
          <SpacedItems.Container
            direction="vertical"
            css={css`
              height: 100%;
            `}
          >
            <SpacedItems.Item grow={1}>
              <p css={paragraphStyle}>
                Set the visibility of live chat on your widgets.
              </p>
              <SortableList headers={sortHeaders} initialSortKey="title">
                {widgets.map(widget => (
                  <Segment raised key={widget.id} title={widget.name}>
                    <SpacedItems.Container verticalAlign="center">
                      <SpacedItems.Item css={widgetNameStyle}>
                        {widget.name}
                      </SpacedItems.Item>
                      <SpacedItems.Item basis="240px" gap="auto">
                        <Form.Group grouped>
                          <Form.Field>
                            <Dropdown
                              options={options}
                              onChange={handleChange}
                              selection
                              value={availability[widget.id]}
                              name={widget.id}
                              fluid
                            />
                          </Form.Field>
                        </Form.Group>
                      </SpacedItems.Item>
                    </SpacedItems.Container>
                  </Segment>
                ))}
              </SortableList>
            </SpacedItems.Item>
            <SpacedItems.Item gap="auto">
              <MessageBox
                dismissFlag="chatVisibilityOverrideBotSettings"
                icon="warning"
                info
              >
                Disabling the chat here overrides any bot settings you have
                enabled on your widget (eg: Office Hours or No Online agents).
              </MessageBox>
              <MessageBox
                dismissFlag="chatVisibilityWidgetStillAppear"
                icon="warning"
                info
              >
                Your widget may still display depending on other integrations on
                your widget. You can manage your widget integrations here.
              </MessageBox>
            </SpacedItems.Item>
          </SpacedItems.Container>
        )}
      </div>
    </Sidebar>
  )
}

export default AvailabilityEditor
